<template lang='pug'>
  v-container.pa-4(fill-height='', fluid='')
    v-progress-linear(v-if="loading" indeterminate color="cyan")

    // Header with filters
    v-row.mb-4
      v-col(cols="12")
        v-card.pa-4
          v-row.align-center
            v-col(cols="12" md="3")
              h2.text-h4.font-weight-light Métricas de Categorías
            v-col(cols="12" md="3")
              v-select(
                v-model="selectedTimeRange"
                :items="timeRangeOptions"
                label="Período"
                outlined
                dense
                @change="loadCategoryData"
              )
            v-col(cols="12" md="3")
              v-menu(
                ref="dateMenu"
                v-model="dateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              )
                template(v-slot:activator="{ on, attrs }")
                  v-text-field(
                    v-model="dateRangeText"
                    label="Rango de fechas"
                    prepend-icon="mdi-calendar"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"
                  )
                v-date-picker(
                  v-model="selectedDates"
                  range
                  @input="dateMenu = false; loadCategoryData()"
                )
            v-col(cols="12" md="3")
              v-btn(color="primary" @click="loadCategoryData" :loading="loading")
                v-icon mdi-refresh
                | Actualizar

    // Category Performance Overview Cards
    v-row.mb-4
      v-col(cols="12" sm="6" md="3")
        base-material-stats-card(
          color="info"
          icon="mdi-tag-multiple"
          :title="'Total Categorías'"
          :value="categoryStats.totalCategories"
          :sub-text="'categorías activas'"
          sub-icon="mdi-trending-up"
          sub-icon-color="success"
        )
      v-col(cols="12" sm="6" md="3")
        base-material-stats-card(
          color="success"
          icon="mdi-currency-usd"
          :title="'Ventas Totales'"
          :value="categoryStats.totalRevenue"
          :sub-text="'+' + categoryStats.revenueGrowth + '% vs período anterior'"
          sub-icon="mdi-trending-up"
          sub-icon-color="success"
        )
      v-col(cols="12" sm="6" md="3")
        base-material-stats-card(
          color="warning"
          icon="mdi-chart-line"
          :title="'Mejor Categoría'"
          :value="categoryStats.topCategory.name || 'N/A'"
          :sub-text="'$' + (categoryStats.topCategory.revenue || 0)"
          sub-icon="mdi-trophy"
          sub-icon-color="warning"
        )
      v-col(cols="12" sm="6" md="3")
        base-material-stats-card(
          color="error"
          icon="mdi-chart-pie"
          :title="'Promedio Orden'"
          :value="categoryStats.avgOrderValue"
          :sub-text="'por transacción'"
          sub-icon="mdi-calculator"
          sub-icon-color="info"
        )

    // Charts Section
    v-row.mb-4
      v-col(cols="12" md="8")
        base-material-chart-card(
          color="success"
          :data="categoryRevenueChartData"
          :options="chartOptions"
          type="Line"
        )
          template(v-slot:reveal-actions)
            v-tooltip(bottom)
              template(v-slot:activator="{ on }")
                v-btn(
                  v-on="on"
                  color="info"
                  icon
                  small
                )
                  v-icon(small) mdi-refresh
              span Actualizar datos
          h4.text-h4.font-weight-light Ingresos por Categoría
          p.text-body-2.font-weight-light Tendencia de ventas por categorías principales

      v-col(cols="12" md="4")
        v-card.pa-4(height="350")
          h4.text-h6.mb-3 Distribución de Ventas
          pie-chart(
            :chart-data="categoryDistributionData"
            :options="pieChartOptions"
            style="height: 250px;"
          )

    // Category Performance Tables
    v-row.mb-4
      v-col(cols="12" md="6")
        v-card
          v-card-title
            v-icon.mr-2(color="success") mdi-trending-up
            | Top Categorías Rendimiento
          v-data-table(
            :headers="topCategoriesHeaders"
            :items="topCategories"
            :items-per-page="5"
            hide-default-footer
          )
            template(v-slot:item.growth="{ item }")
              v-chip(
                :color="item.growth >= 0 ? 'success' : 'error'"
                text-color="white"
                small
              ) {{ item.growth >= 0 ? '+' : '' }}{{ item.growth }}%
            template(v-slot:item.revenue="{ item }")
              span.font-weight-bold ${{ item.revenue | numberFormat }}

      v-col(cols="12" md="6")
        v-card
          v-card-title
            v-icon.mr-2(color="warning") mdi-chart-bar
            | Análisis de Categorías
          v-data-table(
            :headers="categoryAnalysisHeaders"
            :items="categoryAnalysis"
            :items-per-page="5"
            hide-default-footer
          )
            template(v-slot:item.margin="{ item }")
              v-progress-linear(
                :value="item.margin"
                :color="item.margin > 20 ? 'success' : item.margin > 10 ? 'warning' : 'error'"
                height="6"
              )
                template(v-slot:default="{ value }")
                  small {{ Math.ceil(value) }}%
            template(v-slot:item.velocity="{ item }")
              v-rating(
                :value="item.velocity"
                color="amber"
                dense
                half-increments
                readonly
                size="16"
              )

    // Detailed Category Insights
    v-row.mb-4
      v-col(cols="12")
        v-expansion-panels
          v-expansion-panel
            v-expansion-panel-header
              template(v-slot:default="{ open }")
                v-row(no-gutters)
                  v-col(cols="4")
                    strong Insights Detallados de Categorías
                  v-col(cols="8" class="text--secondary")
                    v-fade-transition(leave-absolute)
                      span(v-if="!open") Ver análisis avanzado y recomendaciones
            v-expansion-panel-content
              category-insights(
                :category-data="categoryAnalytics"
                :time-range="selectedTimeRange"
              )

    // Original Reports Section (moved to bottom)
    v-row(v-if="reports.length > 0")
      v-col(cols="12")
        v-card
          v-card-title
            v-icon.mr-2 mdi-file-excel
            | Reportes Exportables
          v-data-table.mx-auto(:headers="headers" :items="reports")
            template(v-slot:item.name="{ item }")
              v-row
               v-icon(medium) mdi-file-excel
               p {{item.name}}
            template(v-slot:item.url="{ item }")
              v-btn( text color='primary' :href="item.url" download)
                v-icon mdi-arrow-down

    template(v-if="!loading && categoryStats.totalCategories === 0")
      NoResults(title="No hay datos de categorías", description="Los datos de métricas se generarán cuando tengas ventas registradas")
</template>
<script>
  import loaders from '@/plugins/mixins/loaders'
  import Vue from 'vue'

  export default {
    components: {
      NoResults: () => import('@/components/custom/NoResults'),
      PieChart: () => import('@/components/custom/charts/PieChart'),
      CategoryInsights: () => import('@/components/custom/analytics/CategoryInsights'),
      BaseMaterialStatsCard: () => import('@/components/base/MaterialStatsCard'),
      BaseMaterialChartCard: () => import('@/components/base/MaterialChartCard'),
    },
    filters: {
      numberFormat (val) {
        if (val) {
          return val.toLocaleString()
        }
        return '0'
      },
    },
    mixins: [loaders],
    data () {
      return {
        selectedTimeRange: 'daily',
        dateMenu: false,
        selectedDates: [
          Vue.moment().subtract(7, 'days').format('YYYY-MM-DD'),
          Vue.moment().format('YYYY-MM-DD'),
        ],
        timeRangeOptions: [
          { text: 'Diario', value: 'daily' },
          { text: 'Mensual', value: 'monthly' },
        ],
        chartOptions: {
          maintainAspectRatio: false,
          legend: {
            display: false,
          },
          responsive: true,
          tooltips: {
            backgroundColor: '#f5f5f5',
            titleFontColor: '#333',
            bodyFontColor: '#666',
            bodySpacing: 4,
            xPadding: 12,
            mode: 'nearest',
            intersect: 0,
            position: 'nearest',
          },
          scales: {
            yAxes: [{
              ticks: {
                suggestedMin: 0,
                callback: function (value) {
                  return '$' + value.toLocaleString()
                },
              },
            }],
            xAxes: [{
              ticks: {
                display: true,
              },
            }],
          },
        },
        pieChartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            position: 'bottom',
            labels: {
              usePointStyle: true,
              padding: 20,
            },
          },
          tooltips: {
            callbacks: {
              label: function (tooltipItem, data) {
                const label = data.labels[tooltipItem.index]
                const value = data.datasets[0].data[tooltipItem.index]
                const total = data.datasets[0].data.reduce((a, b) => a + b, 0)
                const percentage = ((value / total) * 100).toFixed(1)
                return `${label}: $${value.toLocaleString()} (${percentage}%)`
              },
            },
          },
        },
        topCategoriesHeaders: [
          { text: 'Categoría', value: 'name', sortable: true },
          { text: 'Ingresos', value: 'revenue', sortable: true },
          { text: 'Transacciones', value: 'transactions', sortable: true },
          { text: 'Crecimiento', value: 'growth', sortable: true },
        ],
        categoryAnalysisHeaders: [
          { text: 'Categoría', value: 'name', sortable: true },
          { text: 'Margen (%)', value: 'margin', sortable: true },
          { text: 'Velocidad', value: 'velocity', sortable: true },
          { text: 'Stock Rotation', value: 'rotation', sortable: true },
        ],
      }
    },
    computed: {
      loading () {
        return this.$store.state.transactions.isLoading || this.$store.state.summary.isLoading
      },
      reports () {
        return this.$store.state.transactions.summaries
      },
      headers () {
        return [
          {
            sortable: true,
            text: 'Fecha',
            value: 'time',
            align: 'center',
          },
          {
            sortable: false,
            text: 'Titulo',
            value: 'name',
            align: 'left',
          },
          {
            sortable: false,
            text: 'Descargar',
            value: 'url',
            align: 'center',
          },
        ]
      },
      dateRangeText () {
        if (this.selectedDates.length === 2) {
          return `${this.selectedDates[0]} - ${this.selectedDates[1]}`
        }
        return ''
      },
      categoryAnalytics () {
        const daily = this.$store.state.summary.categorySalesDailyList || {}
        const monthly = this.$store.state.summary.categorySalesMonthlyList || {}
        return {
          daily,
          monthly,
        }
      },
      categoryStats () {
        const analytics = this.categoryAnalytics
        const timeData = this.selectedTimeRange === 'daily' ? analytics.daily : analytics.monthly

        if (!timeData || Object.keys(timeData).length === 0) {
          return {
            totalCategories: 0,
            totalRevenue: 0,
            revenueGrowth: 0,
            topCategory: {},
            avgOrderValue: 0,
          }
        }

        // Process category data to calculate stats
        const categoryTotals = {}
        let totalRevenue = 0
        let totalTransactions = 0

        Object.keys(timeData).forEach(date => {
          const dateData = timeData[date]
          Object.keys(dateData).forEach(categoryId => {
            const categoryData = dateData[categoryId]
            if (!categoryTotals[categoryId]) {
              categoryTotals[categoryId] = {
                name: categoryData.categoryName || `Categoría ${categoryId}`,
                revenue: 0,
                transactions: 0,
                quantity: 0,
              }
            }
            categoryTotals[categoryId].revenue += categoryData.totalRevenue || 0
            categoryTotals[categoryId].transactions += categoryData.transactionCount || 0
            categoryTotals[categoryId].quantity += categoryData.totalQuantity || 0
            totalRevenue += categoryData.totalRevenue || 0
            totalTransactions += categoryData.transactionCount || 0
          })
        })

        const categories = Object.values(categoryTotals)
        const topCategory = categories.length > 0
          ? categories.reduce((max, cat) => cat.revenue > max.revenue ? cat : max, categories[0]) : {}

        return {
          totalCategories: categories.length,
          totalRevenue: Math.round(totalRevenue),
          revenueGrowth: this.calculateGrowth(totalRevenue),
          topCategory,
          avgOrderValue: totalTransactions > 0 ? Math.round(totalRevenue / totalTransactions) : 0,
        }
      },
      categoryRevenueChartData () {
        const analytics = this.categoryAnalytics
        const timeData = this.selectedTimeRange === 'daily' ? analytics.daily : analytics.monthly

        if (!timeData || Object.keys(timeData).length === 0) {
          return {
            labels: [],
            datasets: [],
          }
        }

        // Get sorted dates
        const dates = Object.keys(timeData).sort()
        const labels = dates.map(date => {
          return this.selectedTimeRange === 'daily'
            ? Vue.moment(date).format('DD/MM')
            : Vue.moment(date).format('MMM YYYY')
        })

        // Get top 5 categories by revenue
        const categoryTotals = this.getTopCategories(5)
        const colors = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336']

        const datasets = categoryTotals.map((category, index) => ({
          label: category.name,
          data: dates.map(date => {
            const dateData = timeData[date] || {}
            const categoryData = Object.values(dateData).find(cat =>
              (cat.categoryName || `Categoría ${cat.categoryId}`) === category.name,
            )
            return categoryData ? categoryData.totalRevenue || 0 : 0
          }),
          borderColor: colors[index],
          backgroundColor: colors[index] + '20',
          fill: false,
          tension: 0.1,
        }))

        return {
          labels,
          datasets,
        }
      },
      categoryDistributionData () {
        const topCategories = this.getTopCategories(6)
        const colors = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#607D8B']

        return {
          labels: topCategories.map(cat => cat.name),
          datasets: [{
            data: topCategories.map(cat => cat.revenue),
            backgroundColor: colors,
            borderWidth: 2,
            borderColor: '#fff',
          }],
        }
      },
      topCategories () {
        return this.getTopCategories(5).map(category => ({
          ...category,
          growth: this.calculateCategoryGrowth(category.name),
        }))
      },
      categoryAnalysis () {
        const categories = this.getTopCategories(5)
        return categories.map(category => ({
          name: category.name,
          margin: this.calculateMargin(category),
          velocity: this.calculateVelocity(category),
          rotation: this.calculateRotation(category),
        }))
      },
    },
    created () {
      this.loadLog()
      this.loadCategoryData()
    },
    methods: {
      loadLog () {
        const vm = this
        vm.$store.dispatch('transactions/loadLogSummary').then(function () {})
      },
      loadCategoryData () {
        this.$store.commit('summary/SET_LOADING', true)

        const dateFrom = this.selectedDates[0]
        const dateTo = this.selectedDates[1]

        this.$store.dispatch('summary/loadCategoryAnalytics', {
          timeRange: this.selectedTimeRange,
          dateFrom,
          dateTo,
        }).catch(error => {
          console.error('Error loading category data:', error)
          this.$store.commit('summary/SET_LOADING', false)
        })
      },
      getTopCategories (limit = 10) {
        const analytics = this.categoryAnalytics
        const timeData = this.selectedTimeRange === 'daily' ? analytics.daily : analytics.monthly

        if (!timeData || Object.keys(timeData).length === 0) {
          return []
        }

        const categoryTotals = {}
        Object.keys(timeData).forEach(date => {
          const dateData = timeData[date]
          Object.keys(dateData).forEach(categoryId => {
            const categoryData = dateData[categoryId]
            const categoryName = categoryData.categoryName || `Categoría ${categoryId}`
            if (!categoryTotals[categoryName]) {
              categoryTotals[categoryName] = {
                name: categoryName,
                revenue: 0,
                transactions: 0,
                quantity: 0,
              }
            }
            categoryTotals[categoryName].revenue += categoryData.totalRevenue || 0
            categoryTotals[categoryName].transactions += categoryData.transactionCount || 0
            categoryTotals[categoryName].quantity += categoryData.totalQuantity || 0
          })
        })

        return Object.values(categoryTotals)
          .sort((a, b) => b.revenue - a.revenue)
          .slice(0, limit)
      },
      calculateGrowth (currentValue) {
        // Mock growth calculation - in real app would compare with previous period
        return Math.floor(Math.random() * 20) - 5 // Random -5% to +15%
      },
      calculateCategoryGrowth (categoryName) {
        // Mock growth calculation for specific category
        return Math.floor(Math.random() * 30) - 10 // Random -10% to +20%
      },
      calculateMargin (category) {
        // Mock margin calculation - in real app would use cost data
        return Math.floor(Math.random() * 50) + 10 // Random 10% to 60%
      },
      calculateVelocity (category) {
        // Mock velocity rating based on transaction frequency
        const baseVelocity = Math.min(category.transactions / 10, 5)
        return Math.max(1, baseVelocity)
      },
      calculateRotation (category) {
        // Mock stock rotation calculation
        return Math.floor(Math.random() * 12) + 1 // Random 1-12 times per period
      },
    },
  }
</script>
